import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  Rocket, Check, MapPin, ArrowRight, Star, Clock, Globe2, ShieldCheck, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConvertKitHeroForm } from "@/components/ConvertKitHeroForm";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { SiteHeader, SiteFooter } from "@/components/blog/SiteHeader";
import { Testimonials } from "@/components/Testimonials";
import heroPeople from "@/assets/hero-people.jpg";
import sierraDesk from "@/assets/sierra-desk.jpg";
import portfolioSpeakingAuthor from "@/assets/portfolio/speaking-author-sharp.jpg";
import portfolioRealtor from "@/assets/portfolio/realtor.png";
import portfolioCoach from "@/assets/portfolio/maison-noir.png";
import portfolioAdvisor from "@/assets/portfolio/ls-advisory.png";
import portfolioCafe from "@/assets/portfolio/corner-cuppa.png";
import portfolioLandscape from "@/assets/portfolio/jameson-sons.png";

const SITE = "https://threedaylaunch.com";
const PATH = "/website-designer-near-me";
const TITLE = "Website Designer Near Me: Custom Sites Launched in 3 Days";
const DESCRIPTION =
  "Looking for a website designer near you? Three Day Launch builds custom, mobile-ready, SEO-optimized websites for local businesses and coaches—live in just 3 days.";
const OG_IMAGE = `${SITE}${sierraDesk}`;

const bookingUrl =
  "/book";

const reasons = [
  {
    icon: Clock,
    title: "Launched in 3 days, not 3 months",
    body: "Most local web designers take weeks. We design, build, and publish your custom site in just 72 hours so you stop losing leads to a stale site.",
  },
  {
    icon: Sparkles,
    title: "Custom design, never a template",
    body: "Your website is hand-designed around your brand, services, and ideal client—not a drag-and-drop theme you've seen on every other local business.",
  },
  {
    icon: Globe2,
    title: "SEO baked in from day one",
    body: "Proper title tags, meta descriptions, fast load times, semantic HTML, and mobile responsiveness so customers near you actually find you on Google.",
  },
  {
    icon: ShieldCheck,
    title: "Hosting, security & support handled",
    body: "Optional Care Plans cover secure hosting, edits, backups, and uptime monitoring so you can run your business, not your website.",
  },
];

const services = [
  "Local service businesses (salons, contractors, landscapers, plumbers)",
  "Coaches, consultants, and online educators",
  "Realtors and real estate teams",
  "Cafés, restaurants & boutique retail",
  "Medical, dental, and wellness practices",
  "Photographers, creatives, and studios",
];

const portfolio = [
  { src: portfolioSpeakingAuthor, alt: "Custom author and speaking website designed by Three Day Launch" },
  { src: portfolioRealtor, alt: "Modern realtor website design with lead-gen forms and listings" },
  { src: portfolioCoach, alt: "Coaching website design with bold brand identity and client onboarding" },
  { src: portfolioAdvisor, alt: "Financial advisor website design with clean trust-building layout" },
  { src: portfolioCafe, alt: "Local café website design with menu, hours, and online ordering links" },
  { src: portfolioLandscape, alt: "Landscaping company website design with service area and quote form" },
];

const faqs = [
  {
    q: "Do I need to live near you to hire Three Day Launch?",
    a: "No. We work with local business owners across the United States and beyond. Everything happens over Zoom, email, and a shared design portal—so your 'website designer near me' search ends here, no matter where you are.",
  },
  {
    q: "What does a custom website actually cost?",
    a: "Most local web designers charge $3,000–$10,000+ and take 6–12 weeks. We launch a custom, multi-page, SEO-ready website in 3 days, and most projects land between $1,000 and $4,000—book a free call and we'll quote your project transparently.",
  },
  {
    q: "How can you really launch a website in three days?",
    a: "Day 1 is your kickoff call where we lock in strategy, copy direction, and brand. Day 2 is design and revisions. Day 3 we connect your domain, configure hosting, and push live. You see progress every day.",
  },
  {
    q: "Will my new website rank on Google?",
    a: "Every Three Day Launch site ships with on-page SEO baked in: optimized title tags, meta descriptions, alt text, structured data, fast Core Web Vitals scores, and mobile-first responsive design—the foundation Google rewards.",
  },
  {
    q: "Do you handle hosting and updates after launch?",
    a: "Yes. Our Website Care Plans handle managed hosting, security, backups, performance monitoring, and content edits so you never have to touch a CMS dashboard.",
  },
  {
    q: "What kinds of businesses do you work with?",
    a: "Coaches, consultants, realtors, salons, restaurants, contractors, landscapers, medical practices, and most service-based local businesses. We do not build e-commerce stores.",
  },
];

export const Route = createFileRoute("/website-designer-near-me")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: `${SITE}${PATH}` },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "Three Day Launch — Website Designer",
          image: OG_IMAGE,
          url: `${SITE}${PATH}`,
          priceRange: "$$",
          description: DESCRIPTION,
          address: {
            "@type": "PostalAddress",
            addressRegion: "IL",
            addressCountry: "US",
          },
          areaServed: "United States",
          founder: {
            "@type": "Person",
            name: "Sierra Janisse",
            jobTitle: "Founder & Web Designer",
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "5",
            reviewCount: "100",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Custom website design",
          provider: { "@type": "Organization", name: "Three Day Launch", url: SITE },
          areaServed: "United States",
          name: "Custom website design, launched in 3 days",
          description:
            "Custom website design for local business owners and coaches. Multi-page, mobile responsive, SEO-ready, launched in 3 days.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Website Designer Near Me", item: `${SITE}${PATH}` },
          ],
        }),
      },
    ],
  }),
  component: NearMePage,
});

function NearMePage() {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden pt-20 pb-20 md:pt-28 md:pb-28">
        <img
          src={heroPeople}
          alt="Three Day Launch website designer team helping a local business owner launch a custom site"
          title="Website designer near me — Three Day Launch"
          width={1600}
          height={1066}
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/45 to-black/70" />

        <div className="relative mx-auto max-w-7xl px-6 grid md:grid-cols-2 gap-12 items-center">
          <div className="text-white min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-wider border border-white/15">
              <MapPin className="h-3.5 w-3.5 text-primary" />
              Website Designer · Serving the U.S.
            </div>
            <h1 className="mt-6 text-4xl md:text-6xl leading-[1.1] font-bold">
              The <span className="text-primary">website designer near you</span> who launches in just <span className="text-primary">3 days</span>.
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-xl">
              Custom-designed, mobile-ready, SEO-optimized websites for local businesses and coaches. Done in 72 hours—not 3 months.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Custom designed for your brand — never a template",
                "Multi-page, mobile responsive, fast-loading",
                "Built-in SEO so customers actually find you",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3 text-white/90">
                  <span className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center">
                    <Check className="h-3.5 w-3.5 text-primary" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="bg-primary hover:opacity-90 text-primary-foreground font-semibold h-14 px-8 rounded-full text-base"
              >
                <a href={bookingUrl}>Book a Free Call <ArrowRight className="ml-2 h-4 w-4" /></a>
              </Button>
            </div>
            <div className="mt-6 flex items-center gap-2 text-sm text-white/80">
              <div className="flex">
                {[0,1,2,3,4].map((i) => (
                  <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                ))}
              </div>
              <span>5-star rated by 100+ local business owners</span>
            </div>
          </div>

          <div className="relative bg-card text-card-foreground rounded-3xl p-8 shadow-card-soft border border-border/40">
            <div className="absolute -top-4 left-8 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
              Free SEO Content Agent™
            </div>
            <h2 className="text-2xl font-bold mt-2">Get more local leads from Google</h2>
            <p className="mt-2 text-sm text-muted-foreground">Plan, create, and optimize content to attract clients near you.</p>
            <ConvertKitHeroForm idPrefix="lead-near-me" websitePlaceholder="https://yourbusiness.com" />
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-20 md:py-24 bg-background">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-primary">Why Three Day Launch</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-bold tracking-tight">
              The local website designer built for speed, conversions, and SEO.
            </h2>
            <p className="mt-5 text-lg text-muted-foreground">
              Searching for "website designer near me" usually turns up agencies that take months and charge five figures. We're the alternative: a boutique web design studio that ships beautiful, lead-generating websites in three days.
            </p>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {reasons.map((r) => (
              <div key={r.title} className="rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition">
                <div className="h-11 w-11 rounded-xl bg-primary/15 flex items-center justify-center text-primary">
                  <r.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-semibold">{r.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who we serve */}
      <section className="py-20 md:py-24 bg-secondary text-secondary-foreground">
        <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src={sierraDesk}
              alt="Sierra Janisse, founder of Three Day Launch, designing a custom website for a local business client"
              title="Three Day Launch — local website designer"
              width={1200}
              height={900}
              loading="lazy"
              decoding="async"
              className="rounded-3xl shadow-card-soft border border-white/10"
            />
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-primary">Who we serve</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
              The go-to website designer for service-based businesses across the U.S.
            </h2>
            <p className="mt-5 text-white/80">
              Whether you run a brick-and-mortar shop or a fully remote coaching practice, we design websites that turn visitors into paying clients.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {services.map((s) => (
                <li key={s} className="flex items-start gap-3 text-white/90">
                  <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-sm">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Portfolio */}
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-primary">Recent work</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-bold tracking-tight">
              Custom websites we've designed for businesses like yours.
            </h2>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {portfolio.map((p) => (
              <PortfolioMockup key={p.src} src={p.src} alt={p.alt} />
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button asChild size="lg" className="bg-primary hover:opacity-90 text-primary-foreground font-semibold h-14 px-8 rounded-full">
              <Link to="/portfolio">See the full portfolio <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-24 bg-muted/30">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-primary">The Three Day Launch process</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-bold tracking-tight">
              How we build your custom website in 72 hours.
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              { day: "Day 1", title: "Kickoff & strategy", body: "A 60-minute Zoom call to lock in your goals, audience, brand direction, and site structure." },
              { day: "Day 2", title: "Design & revisions", body: "We design your pages and walk you through real-time feedback rounds—no surprises, no waiting weeks." },
              { day: "Day 3", title: "Launch day", body: "We connect your domain, configure hosting, verify SEO basics, and push your site live to the world." },
            ].map((s) => (
              <div key={s.day} className="rounded-2xl border border-border bg-card p-7">
                <div className="text-xs font-bold uppercase tracking-wider text-primary">{s.day}</div>
                <h3 className="mt-2 text-xl font-bold">{s.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      {/* FAQ */}
      <section id="faq" className="py-20 md:py-24">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <p className="text-sm font-semibold uppercase tracking-wider text-primary">Frequently asked</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-bold tracking-tight">
              Website designer near me — your questions, answered.
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-24 bg-hero-gradient text-white">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-wider border border-white/15">
            <Rocket className="h-3.5 w-3.5 text-primary" />
            Ready to launch?
          </div>
          <h2 className="mt-6 text-3xl md:text-5xl font-bold tracking-tight">
            Stop searching for a "website designer near me." Start building.
          </h2>
          <p className="mt-5 text-white/85 text-lg">
            Book a free call and we'll map out exactly what your custom website needs to bring in more local leads—then launch it in 3 days.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="bg-primary hover:opacity-90 text-primary-foreground font-semibold h-14 px-8 rounded-full text-base">
              <a href={bookingUrl}>Book a Free Call <ArrowRight className="ml-2 h-4 w-4" /></a>
            </Button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function PortfolioMockup({ src, alt }: { src: string; alt: string }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const [isTouch, setIsTouch] = useState(false);
  const [autoScrolling, setAutoScrolling] = useState(false);
  const stoppedRef = useRef(false);
  const dragRef = useRef<{ y: number; top: number; active: boolean } | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setIsTouch(window.matchMedia("(hover: none)").matches);
  }, []);

  const stopAnim = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const animateTo = (target: number, duration: number) => {
    const el = scrollRef.current;
    if (!el) return;
    stopAnim();
    const start = el.scrollTop;
    const delta = target - start;
    const t0 = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / duration);
      el.scrollTop = start + delta * p;
      if (p < 1) rafRef.current = requestAnimationFrame(step);
      else rafRef.current = null;
    };
    rafRef.current = requestAnimationFrame(step);
  };

  const handleEnter = () => {
    const el = scrollRef.current;
    if (!el || isTouch) return;
    const max = el.scrollHeight - el.clientHeight;
    if (max <= 0) return;
    animateTo(max, Math.max(2000, max * 8));
  };

  const handleLeave = () => {
    if (isTouch) return;
    if (stoppedRef.current) return;
    stopAnim();
    animateTo(0, 600);
  };

  // Click/tap anywhere on the mockup stops the auto-scroll (mobile + desktop)
  const handleStop = () => {
    stopAnim();
    stoppedRef.current = true;
    setAutoScrolling(false);
  };

  // Drag to scroll manually
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    handleStop();
    const el = scrollRef.current;
    if (!el) return;
    dragRef.current = { y: e.clientY, top: el.scrollTop, active: true };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    const d = dragRef.current;
    if (!el || !d?.active) return;
    el.scrollTop = d.top - (e.clientY - d.y);
  };

  const endDrag = () => {
    if (dragRef.current) dragRef.current.active = false;
  };

  useEffect(() => () => stopAnim(), []);

  return (
    <div className="group relative rounded-2xl border border-border bg-card overflow-hidden">
      <div
        ref={scrollRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={handleStop}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="aspect-[4/3] w-full overflow-y-auto overflow-x-hidden touch-pan-y scrollbar-thin [-webkit-overflow-scrolling:touch]"
        aria-label={isTouch ? "Drag to scroll the website mockup" : alt}
      >
        <img
          src={src}
          alt={alt}
          title={alt}
          loading="lazy"
          decoding="async"
          width={800}
          className="block w-full h-auto select-none pointer-events-none"
          draggable={false}
        />
      </div>
      {isTouch && !autoScrolling && (
        <div className="pointer-events-none absolute bottom-2 right-2 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
          Drag to scroll
        </div>
      )}
    </div>
  );
}
